/**
 * SiteDashboardPage (CLOUD) — the selected site at a glance, as the same board of cards the
 * local product opens on.
 *
 * It exists so "Dashboard" means something in both scopes. All Sites answers "which site needs
 * me"; this answers "how is THIS site" — and an operator who has switched scope to one site
 * shouldn't have to go back to the estate view and hunt for its row to see its numbers.
 *
 * The board itself — cards, filters, lists, record modals — is the shared `DashboardBoardPage`,
 * so a card added or changed there lands on both products at once. This file is only what is
 * CLOUD about it:
 *   - every read goes through the selected site's tunnel (`getNode*`);
 *   - machine load comes from the node registry, which NodeContext already polls — the cloud
 *     knows a node's load without asking it;
 *   - a snapshot is not a URL the browser can load (it points inside the node), so each picture
 *     is streamed through the tunnel as a blob, lazily, once its row is on screen;
 *   - the permission keys are the cloud's spelling (`ai-models.view`, `monitoring.view`).
 *
 * The layout is the operator's, not the site's: one arrangement, kept on the account, serves
 * this board and the All Sites one (useAccountDashboardLayout).
 *
 * The board is keyed on the site, so switching sites remounts it: no figure, list or open
 * record from the previous site survives the switch. An offline site gets a sentence instead —
 * everything on the board lives on the far end of a tunnel that isn't there.
 */
import { useMemo, useRef } from 'react';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import {
  DashboardBoardPage,
  PageTitle,
  formatUptime,
  reportModelSlug,
  type DashboardBoardHost,
  type DashboardMachineRead,
} from '@trinetra/shared';
import { AlertDetailView } from './AlertDetailPage';
import { EventDetailBody } from './AlertInvestigationPage';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import { useAccountDashboardLayout } from '../hooks/useAccountDashboardLayout';
import {
  getNodeCameras,
  getNodeGroups,
  getNodeModelAssignments,
  getNodeAiSummary,
  getNodeModelEvents,
  getNodeModelEvent,
  getNodeModelStats,
  getNodeModelEventImage,
} from '../services/api.service';

/** Value of a fulfilled settled result, or undefined if it rejected. */
const settled = (r: PromiseSettledResult<any>) => (r.status === 'fulfilled' ? r.value : undefined);

/** The node proxy answers with a bare array, `{data}` or `{data, pagination}`. */
function toArray(res: any): any[] {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

export default function SiteDashboardPage() {
  const { nodes, selectedNodeId } = useNode();
  const { hasPermission } = useAuth();
  // The operator's one layout — the All Sites dashboard draws the same board with it.
  const { layoutKey, layoutRemote } = useAccountDashboardLayout();

  // Read the node out of `nodes` rather than `selectedNode`: that object is memoised on
  // id/status/name for reference stability, so its `metrics` would be whatever the poll happened
  // to carry when the name last changed — stale by minutes on a page whose job is load.
  const node = useMemo(
    () => nodes.find((n) => n.nodeId === selectedNodeId) || null,
    [nodes, selectedNodeId],
  );
  // The board's 30 s poll reads the registry's LATEST metrics through this, not the ones from
  // the render that built the host.
  const nodeRef = useRef(node);
  nodeRef.current = node;
  const isOnline = node?.status === 'online';

  // The record bodies are component TYPES; built once per site, because a new type every render
  // (the registry poll re-renders this page) would remount an open record and refetch it.
  const records = useMemo(() => {
    const nodeId = selectedNodeId || '';
    const EventRecord: DashboardBoardHost['EventRecord'] = ({ event, model, cameraNameMap, personName, headerActions, onClose }) => (
      <EventDetailBody
        event={event}
        model={model}
        nodeId={nodeId}
        cameraNameMap={cameraNameMap}
        personName={(e: any) => personName || e?.matched_person_name || null}
        headerActions={headerActions}
        onClose={onClose}
      />
    );
    const AlertRecord: DashboardBoardHost['AlertRecord'] = ({ alertId, headerActions, onClose }) => (
      <AlertDetailView alertId={alertId} showTitle={false} nodeId={nodeId} headerActions={headerActions} onClose={onClose} />
    );
    return { EventRecord, AlertRecord };
  }, [selectedNodeId]);

  if (!selectedNodeId || !isOnline) {
    return (
      <div className="space-y-4 p-3 sm:p-6">
        <PageTitle
          title={node?.name || 'Dashboard'}
          subtitle={!selectedNodeId ? 'Select a site to see its summary.' : 'This site is offline.'}
        />
        {!selectedNodeId ? (
          <div className="rounded-xl border border-border bg-card p-5 text-[13px] text-muted-foreground shadow-card">
            No site selected. Pick one from the site switcher in the title bar.
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-3 shadow-card">
            <div className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-3 text-[12px] text-muted-foreground">
              <NoSymbolIcon className="h-4 w-4 flex-none" />
              <span>Offline — cameras, alerts and metrics are unavailable until this site reconnects.</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  const nodeId = selectedNodeId;
  const can = {
    cameras: hasPermission('cameras.view'),
    groups: hasPermission('groups.view'),
    models: hasPermission('ai-models.view'),
    assignModels: hasPermission('ai-models.assign'),
    alerts: hasPermission('alerts.view'),
    monitor: hasPermission('monitoring.view'),
    // One key for every analytics card: the node's command-center has no per-user camera
    // scope, so these are site-wide figures for anyone entitled to see AI events at all.
    aiEvents: hasPermission('ai_events.view'),
    attendance: hasPermission('attendance.view'),
  };
  const uptime = node?.metrics?.uptimeSeconds;

  const host: DashboardBoardHost = {
    layoutKey,
    layoutRemote,
    scopeKey: nodeId,
    title: node?.name || 'Dashboard',
    subtitle: `This site at a glance${uptime ? ` · up ${formatUptime(uptime)}` : ''}`,
    subjectNoun: 'site',
    can,
    monitorPath: `/node-monitor/${nodeId}`,

    loadMachine: async (): Promise<DashboardMachineRead> => {
      const [cams, groups, assignments] = await Promise.allSettled([
        can.cameras ? getNodeCameras(nodeId) : Promise.resolve([]),
        can.groups ? getNodeGroups(nodeId) : Promise.resolve([]),
        can.models ? getNodeModelAssignments(nodeId) : Promise.resolve({}),
      ]);
      return {
        cameras: toArray(settled(cams)),
        groups: toArray(settled(groups)),
        assignments: settled(assignments) || {},
        // From the registry, not a fourth tunnel call — and only for those who may see it.
        metrics: can.monitor ? nodeRef.current?.metrics || {} : {},
        failed: [
          cams.status === 'rejected' && 'cameras',
          groups.status === 'rejected' && 'groups',
          assignments.status === 'rejected' && 'AI models',
        ].filter(Boolean) as string[],
      };
    },
    getSummary: (surfaces, opts) => getNodeAiSummary(nodeId, surfaces, opts),
    getModelEvents: (model, params) => getNodeModelEvents(nodeId, model, params),
    getModelEvent: (model, id) => getNodeModelEvent(nodeId, model, id),
    getModelStats: (model, params) => getNodeModelStats(nodeId, model, params),
    /* Streamed through the tunnel, one row at a time and only as each is about to be on screen
       — the board's lazy loader owns that. The cloud has no route to a node's object store, so
       the image KEY is passed as the image TYPE and the node resolves it back to a file.

       A tunnel 404 or timeout still resolves as a Blob — of JSON or text — under
       responseType:'blob', so only real image bytes become an object URL; anything else is no
       picture, and the row keeps its badge rather than showing a broken image. */
    picture: ({ model, eventId, key }) => ({
      loadImage: async () => {
        try {
          const blob: any = await getNodeModelEventImage(nodeId, reportModelSlug(model), eventId, key);
          if (!blob || (blob.type && !blob.type.startsWith('image/'))) return null;
          return URL.createObjectURL(blob);
        } catch {
          return null;
        }
      },
    }),
    EventRecord: records.EventRecord,
    AlertRecord: records.AlertRecord,
  };

  return <DashboardBoardPage key={nodeId} host={host} />;
}
